import { Component, ViewEncapsulation, AfterViewInit, ElementRef, OnDestroy, ViewChild, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { UserService } from 'src/app/services/user/user.service';
import { RoleService } from 'src/app/services/role/role.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { CompanyAgentService } from 'src/app/services/company-agent/company-agent.service';
import { GeneralService } from 'src/app/services/general/general.service';
import Swal from 'sweetalert2';

declare const $: any;
declare var bootstrap: any;

@Component({
  selector: 'app-add-user-modal',
  templateUrl: './add-user-modal.component.html',
  styleUrls: ['./add-user-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddUserModalComponent extends DestructorComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() userAdded = new EventEmitter<void>();

  countryList: any[] = [];
  rolesList: any[] = [];
  primaryAgentOptions: any[] = [];
  companyAgentOptions: any[] = [];
  userForm: FormGroup;
  isLoading: boolean = false;
  selectedImageFile: File | null = null;
  imagePreviewUrl: string | null = null;
  isImageUploading: boolean = false;
  uploadedImageUrl: string = '';

  @ViewChild('repeaterBasic', { static: true }) repeaterBasic!: ElementRef<HTMLElement>;

  constructor(
    private _userService: UserService,
    private _roleService: RoleService,
    private _globalStateService: GlobalStateService,
    private _companyAgentService: CompanyAgentService,
    private _generalService: GeneralService,
    private fb: FormBuilder
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit() {
    this.getRoles();
    this.getPrimaryAgents();
    this.getCompanyAgents();
    // Subscribe to countries from GlobalStateService
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countryList = countries || [];
      })
    );
  }

  getRoles() {
    this.subscriptions.push(
      this._roleService.GetData("1").subscribe({
        next: (res: any) => {
          this.rolesList = res.data?.data || [];
          console.log('Roles loaded:', this.rolesList);

          // Set default role if roles are available
          if (this.rolesList.length > 0) {
            this.userForm.patchValue({
              role: this.rolesList[0]._id
            });
          }
        },
        error: (error) => {
          console.error('Error fetching roles:', error);
          // Use mock data for testing if API fails
          this.rolesList = this.getMockRoles();
        }
      })
    );
  }

  getMockRoles() {
    return [
      { _id: '1', name: 'Administrator' },
      { _id: '2', name: 'Manager' },
      { _id: '3', name: 'Agent' },
      { _id: '4', name: 'Support' }
    ];
  }

  getPrimaryAgents() {
    this.subscriptions.push(
      this._userService.GetData("1", "", { paginate: 'false', is_primary_agent: true, role_type:30 }).subscribe({
        next: (res: any) => {
          if (res.status && res.data) {
            // Handle non-paginated response structure
            const agents = Array.isArray(res.data) ? res.data : res.data.data || [];
            this.primaryAgentOptions = agents.map((agent: any) => ({
              value: agent._id || agent.doc_id || '',
              label: `${agent.title || ''} ${agent.name || agent.login_id || 'Unknown Agent'}`.trim()
            }));
            console.log('Primary agents loaded:', this.primaryAgentOptions);
          } else {
            console.log('Invalid response structure:', res);
            this.primaryAgentOptions = [];
          }
        },
        error: (error) => {
          console.error('Error fetching primary agents:', error);
          this.primaryAgentOptions = [];
        }
      })
    );
  }

  getCompanyAgents() {
    this.subscriptions.push(
      this._companyAgentService.GetData("1", "", { paginate: false }).subscribe({
        next: (res: any) => {
          if (res.status && res.data) {
            // Handle non-paginated response structure
            const agents = Array.isArray(res.data) ? res.data : res.data.data || [];
            this.companyAgentOptions = agents.map((agent: any) => ({
              value: agent._id || '',
              label: agent.name || 'Unknown Agent'
            }));
            console.log('Company agents loaded:', this.companyAgentOptions);
          } else {
            console.log('Invalid response structure:', res);
            this.companyAgentOptions = [];
          }
        },
        error: (error) => {
          console.error('Error fetching company agents:', error);
          this.companyAgentOptions = [];
        }
      })
    );
  }

  initializeForm() {
    this.userForm = this.fb.group({
      avatar: [''],
      title: ['Mr.', [Validators.required]],
      name: ['', [Validators.required]],
      prefix: [''],
      login_id: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirm_password: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
      email_2: ['', [Validators.email]],
      phone_2: [''],
      mobile: [''],
      fax: [''],
      country: [''],
      address: [''],
      city: [''],
      state: [''],
      zip: [''],
      designation: ['', [Validators.required]],
      status: ['', [Validators.required]],
      is_primary_agent: ['', [Validators.required]],
      primary_agent: [''],
      company_agent_id: [''],
      role: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    // Add conditional validator for primary_agent
    this.userForm.get('is_primary_agent')?.valueChanges.subscribe(value => {
      const primaryAgentControl = this.userForm.get('primary_agent');
      if (value === 'no') {
        primaryAgentControl?.setValidators([Validators.required]);
      } else {
        primaryAgentControl?.clearValidators();
        primaryAgentControl?.setValue('');
      }
      primaryAgentControl?.updateValueAndValidity();
    });
  }

  // Custom validator to check if passwords match
  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirm_password');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    if (confirmPassword?.hasError('passwordMismatch')) {
      confirmPassword.setErrors(null);
    }

    return null;
  }

  // Handle image file selection
  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          text: 'Please select a valid image file (PNG, JPG, JPEG)',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        Swal.fire({
          text: 'File size must be less than 5MB',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      this.selectedImageFile = file;

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreviewUrl = e.target.result;
        // Update the image preview in the DOM
        if (this.imagePreviewUrl) {
          this.updateImagePreview(this.imagePreviewUrl);
        }
      };
      reader.readAsDataURL(file);

      // Update form control
      this.userForm.patchValue({ avatar: file.name });

      // Upload the image immediately
      this.uploadImage(file);
    }
  }

  // Upload image to S3
  private uploadImage(file: File): void {
    this.isImageUploading = true;
    this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
      next: (res) => {
        const imageUrl = res.data.url;
        const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

        this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
          next: () => {
            this.isImageUploading = false;
            this.uploadedImageUrl = imageUrl;
            console.log('Image uploaded successfully:', imageUrl);
          },
          error: (error) => {
            this.isImageUploading = false;
            console.error('Error uploading image to S3:', error);
            Swal.fire({
              text: "Error uploading image. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        });
      },
      error: (error) => {
        this.isImageUploading = false;
        console.error('Error getting presigned URL:', error);
        Swal.fire({
          text: "Error uploading image. Please try again.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      }
    });
  }

  // Update the image preview in the DOM
  private updateImagePreview(imageUrl: string): void {
    const imageWrapper = document.querySelector('.image-input-wrapper') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = `url(${imageUrl})`;
    }
  }

  // Remove selected image
  removeImage(): void {
    this.selectedImageFile = null;
    this.imagePreviewUrl = null;
    this.uploadedImageUrl = '';
    this.userForm.patchValue({ avatar: '' });

    // Reset to default image
    const imageWrapper = document.querySelector('.image-input-wrapper') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = 'url(assets/media/avatars/300-6.jpg)';
    }

    // Clear file input
    const fileInput = document.querySelector('input[name="avatar"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  ngAfterViewInit(): void {
    if (this.repeaterBasic?.nativeElement) {
      ($(this.repeaterBasic.nativeElement) as any).repeater({
        initEmpty: false,
        defaultValues: {
          'text-input': 'foo',
        },
        show: function (this: HTMLElement) {
          $(this).slideDown();
        },
        hide: function (this: HTMLElement, deleteElement: () => void) {
          $(this).slideUp(deleteElement);
        },
      });
    }
  }

  onSubmit() {
    if (this.userForm.valid) {
      // Check if image is still uploading
      if (this.isImageUploading) {
        Swal.fire({
          text: "Please wait for the image to finish uploading.",
          icon: "warning",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
        return;
      }

      this.isLoading = true;

      const formValue = this.userForm.value;

      // Log the form data to console as requested
      console.log('Form Data:', formValue);

      // Prepare data for API submission
      const selectedRole = this.rolesList.find(role => role._id === formValue.role);

      const submitData = {
        title: formValue.title,
        name: formValue.name,
        prefix: formValue.prefix,
        login_id: formValue.login_id,
        password: formValue.password,
        email: formValue.email,
        phone: formValue.phone,
        email_2: formValue.email_2,
        phone_2: formValue.phone_2,
        mobile: formValue.mobile,
        fax: formValue.fax,
        country: formValue.country,
        address: formValue.address,
        city: formValue.city,
        state: formValue.state,
        zip: formValue.zip,
        designation: formValue.designation,
        status: formValue.status,
        is_primary_agent: formValue.is_primary_agent === 'yes',
        primary_agent: formValue.is_primary_agent === 'no' ? formValue.primary_agent : null,
        company_agent_id: formValue.company_agent_id,
        role: formValue.role,
        role_name: selectedRole?.name || 'Unknown Role',
        avatar: this.uploadedImageUrl || null
      };

      console.log('Prepared API Data:', submitData);
      console.log('Uploaded Image URL:', this.uploadedImageUrl);

      // Call UserService AddData method
      this.subscriptions.push(
        this._userService.AddData(submitData).subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('User created successfully:', response);

            // Show success message
            Swal.fire({
              text: "Agent has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit event to refresh the listing
                this.userAdded.emit();

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Error creating agent:', error);

            // Show error message
            const errorMessage = error.error?.message ||
                               error.message ||
                               "Sorry, looks like there are some errors detected, please try again.";

            Swal.fire({
              text: errorMessage,
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      // Mark all fields as touched to show validation errors
      this.userForm.markAllAsTouched();
      console.log('Form is invalid:', this.userForm.errors);

      // Log individual field errors
      Object.keys(this.userForm.controls).forEach(key => {
        const control = this.userForm.get(key);
        if (control && control.errors) {
          console.log(`${key} errors:`, control.errors);
        }
      });
    }
  }

  // Helper method to close modal
  closeModal() {
    const modalElement = document.getElementById('kt_modal_add_user');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Helper method to reset form
  resetForm() {
    this.userForm.reset();
    this.initializeForm();

    // Reset image-related properties
    this.selectedImageFile = null;
    this.imagePreviewUrl = null;
    this.uploadedImageUrl = '';
    this.isImageUploading = false;

    // Reset image preview to default
    const imageWrapper = document.querySelector('.image-input-wrapper') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = 'url(assets/media/avatars/300-6.jpg)';
    }

    // Clear file input
    const fileInput = document.querySelector('input[name="avatar"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  ngOnDestroy(): void {
    try {
      if (this.repeaterBasic?.nativeElement) {
        ($(this.repeaterBasic.nativeElement) as any).repeater('destroy');
      }
    } catch { }

    super.ngOnDestroy();
  }
}
